import { useQuery } from '@tanstack/react-query';

import { dashboardApi, dashboardKeys, type DateRange } from '../dashboard.api';

/**
 * Resumen de ventas del rango seleccionado.
 * `enabled` permite no consultar cuando el usuario no tiene el módulo habilitado.
 */
export function useDashboardSummary(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.summary(range),
    queryFn: ({ signal }) => dashboardApi.summary(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Serie diaria de ventas (para la gráfica de barras). */
export function useDailySales(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.dailySales(range),
    queryFn: ({ signal }) => dashboardApi.dailySales(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Ventas por combustible del rango. */
export function useFuelSales(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.fuelSales(range),
    queryFn: ({ signal }) => dashboardApi.fuelSales(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Distribución de ventas por método de pago. */
export function usePaymentMethods(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.paymentMethods(range),
    queryFn: ({ signal }) => dashboardApi.paymentMethods(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Tráfico por hora (24 puntos, incluso las horas sin ventas). */
export function useHourlyTraffic(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.hourlyTraffic(range),
    queryFn: ({ signal }) => dashboardApi.hourlyTraffic(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Rendimiento por manguera de cada lado (el API usa `dispensador`/`pistola`). */
export function useDispenserPerformance(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.dispenserPerformance(range),
    queryFn: ({ signal }) => dashboardApi.dispenserPerformance(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Variación diaria del precio por combustible. */
export function usePriceHistory(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.priceHistory(range),
    queryFn: ({ signal }) => dashboardApi.priceHistory(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Estado actual de los tanques (no depende del rango). */
export function useTanks(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.tanks,
    queryFn: ({ signal }) => dashboardApi.tanks(signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Alertas operativas: tanques críticos y comprobantes rechazados. */
export function useDashboardAlerts(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.alerts,
    queryFn: ({ signal }) => dashboardApi.alerts(signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Compras, gastos y efectivo pendiente de depositar. */
export function useFinancialSummary(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.financialSummary(range),
    queryFn: ({ signal }) => dashboardApi.financialSummary(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Créditos por canjear del rango (ventas a crédito sin CPE emitido). */
export function useCreditosCanjear(range: DateRange, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.creditosCanjear(range),
    queryFn: ({ signal }) => dashboardApi.creditosCanjear(range, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Clientes con mayor saldo por cobrar (no depende del rango). */
export function useTopDebtors(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.topDebtors,
    // Se trae el máximo del API (30) para poder mostrar los 6 primeros en la
    // tarjeta y ofrecer el listado completo en el modal "Ver todos".
    queryFn: ({ signal }) => dashboardApi.topDebtors(signal, 30),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Turnos abiertos (resumen ligero, se refresca cada minuto). */
export function useActiveShifts(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.activeShifts,
    queryFn: ({ signal }) => dashboardApi.activeShifts(signal),
    enabled,
    staleTime: 0,
    refetchInterval: enabled ? 60_000 : false,
  });
}

/** Metas activas del sistema de metas. */
export function useActiveGoals(enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.goals,
    queryFn: ({ signal }) => dashboardApi.goals(signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}
